import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getPaddleEnvironment } from "@/lib/paddle";
import { useSampleDataStatus } from "@/hooks/useSampleData";
import {
  PlanCode,
  EntitlementMap,
  PaddleSubscription,
  UserUsageMonthly,
  Plan,
  FREE_ENTITLEMENTS,
  hasFeature,
  getNumericLimit,
  isLimitReached,
  isUpgradeRequired,
  getRemainingAiCredits,
  getUpgradePlan,
  resolveEffectivePlan,
  FEATURES,
} from "@/lib/plans";
import { format } from "date-fns";

function getCurrentMonthKey(): string {
  return format(new Date(), "yyyy-MM");
}

/**
 * Central hook for subscription state. Reads from the canonical
 * `subscriptions` table (Paddle) with environment-aware filtering.
 *
 * Effective plan resolution:
 *   - active/trialing → paid plan
 *   - canceled but period not ended → still paid
 *   - expired/missing → free
 */
export function useCurrentPlan() {
  const { user } = useAuth();
  const paddleEnv = getPaddleEnvironment();

  // ── Fetch subscription from canonical `subscriptions` table ──
  const {
    data: subscription,
    isLoading: loadingSub,
  } = useQuery({
    queryKey: ["subscription-canonical", user?.id, paddleEnv],
    queryFn: async () => {
      const { data } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user!.id)
        .eq("environment", paddleEnv)
        .maybeSingle();
      return data as unknown as PaddleSubscription | null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Resolve effective plan using centralized resolver
  const resolvedPlanCode: PlanCode = resolveEffectivePlan(subscription ?? null);

  // ── Fetch entitlements for the effective plan ──
  const {
    data: entitlements,
    isLoading: loadingEnt,
  } = useQuery({
    queryKey: ["plan_entitlements", resolvedPlanCode],
    queryFn: async () => {
      const { data } = await supabase
        .from("plan_entitlements")
        .select("feature_key, value_type, value_boolean, value_number, value_text")
        .eq("plan_code", resolvedPlanCode);

      if (!data || data.length === 0) return FREE_ENTITLEMENTS;

      const map: EntitlementMap = {};
      for (const row of data) {
        map[row.feature_key] = row as any;
      }
      return map;
    },
    staleTime: 10 * 60 * 1000,
  });

  // ── Fetch plans ──
  const { data: plans } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const { data } = await supabase
        .from("plans")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      return (data ?? []) as unknown as Plan[];
    },
    staleTime: 30 * 60 * 1000,
  });

  // ── Fetch usage ──
  const { data: usage, isLoading: loadingUsage } = useQuery({
    queryKey: ["user_usage_monthly", user?.id, getCurrentMonthKey()],
    queryFn: async () => {
      const monthKey = getCurrentMonthKey();
      const { data } = await supabase
        .from("user_usage_monthly")
        .select("*")
        .eq("user_id", user!.id)
        .eq("month_key", monthKey)
        .maybeSingle();
      return (data as unknown as UserUsageMonthly) ?? {
        ai_credits_used: 0,
        exports_used: 0,
        assistant_actions_used: 0,
        month_key: monthKey,
      };
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
  });

  const ent = entitlements ?? FREE_ENTITLEMENTS;
  const loading = loadingSub || loadingEnt || loadingUsage;

  // Sample-data exemption: while sample data is loaded, treat every gate
  // as unlocked and every numeric limit as effectively unlimited so the
  // user can explore the full app surface. We override planCode to "elite"
  // so any direct `planCode === "pro" | "elite"` checks throughout the app
  // automatically resolve as fully unlocked.
  const { data: sampleStatus } = useSampleDataStatus();
  const sampleActive = sampleStatus?.hasSample === true;
  const planCode: PlanCode = sampleActive ? "elite" : resolvedPlanCode;

  return {
    // Core state
    planCode,
    resolvedPlanCode,
    subscription,
    entitlements: ent,
    usage: usage ?? null,
    plans: plans ?? [],
    loading,
    sampleActive,

    // Helpers
    hasFeature: (key: string) => (sampleActive ? true : hasFeature(ent, key)),
    getLimit: (key: string, fallback?: number) =>
      sampleActive ? Number.POSITIVE_INFINITY : getNumericLimit(ent, key, fallback),
    isLimitReached: (key: string, count: number) =>
      sampleActive ? false : isLimitReached(ent, key, count),
    isUpgradeRequired: (key: string) =>
      sampleActive ? false : isUpgradeRequired(ent, key),
    remainingAiCredits: getRemainingAiCredits(ent, usage ?? null),
    upgradePlan: getUpgradePlan(planCode),
    isPro: sampleActive ? true : (resolvedPlanCode === "pro" || resolvedPlanCode === "elite"),
    isElite: sampleActive ? true : resolvedPlanCode === "elite",
    isFree: sampleActive ? false : resolvedPlanCode === "free",
    isDemo: sampleActive ? false : resolvedPlanCode === "demo",
    demoExpiresAt: (subscription as any)?.demo_expires_at ?? null,
  };
}
